/**
 * Minimal toast system — used by the F-42 soft-lock notice ("X is editing" +
 * "Edit anyway") and general error surfacing.
 */
import { create } from "zustand";

export interface ToastOptions {
  actionLabel?: string;
  onAction?: () => void;
  durationMs?: number;
}

interface ToastItem {
  id: number;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

interface ToastState {
  toasts: ToastItem[];
  show: (message: string, options?: ToastOptions) => void;
  dismiss: (id: number) => void;
}

let nextToastId = 1;

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  show: (message, options = {}) => {
    const id = nextToastId++;
    const item: ToastItem = { id, message };
    if (options.actionLabel !== undefined) item.actionLabel = options.actionLabel;
    if (options.onAction !== undefined) item.onAction = options.onAction;
    set((s) => ({ toasts: [...s.toasts, item] }));
    window.setTimeout(() => get().dismiss(id), options.durationMs ?? 6000);
  },
  dismiss: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
}));

export function showToast(message: string, options?: ToastOptions): void {
  useToastStore.getState().show(message, options);
}

export function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  if (toasts.length === 0) return null;
  return (
    <div className="toast-host">
      {toasts.map((toast) => (
        <div key={toast.id} className="toast" role="status">
          <span>{toast.message}</span>
          {toast.actionLabel !== undefined && (
            <button
              type="button"
              className="toast-action"
              onClick={() => {
                toast.onAction?.();
                dismiss(toast.id);
              }}
            >
              {toast.actionLabel}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
